import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Box,
  Typography,
  Alert,
  Card,
  CardContent,
} from '@mui/material';
import {
  CloudDownload,
  AttachMoney,
  People,
} from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';
import payrollsService from '../../services/payrolls.service';
import { BankFileExportData } from '../../types/payrolls.types';
import { toast } from 'react-toastify';

const translations = {
  en: {
    title: 'Export Bank File',
    fileName: 'File Name',
    totalAmount: 'Total Amount',
    employeeCount: 'Number of Employees',
    download: 'Download',
    close: 'Close',
    loading: 'Generating file...',
    error: 'Failed to generate file',
    success: 'File downloaded successfully',
    kwd: 'KWD',
    employees: 'employees',
    description: 'This file contains salary transfers for Burgan Bank in the standard format.',
  },
  ar: {
    title: 'تصدير ملف البنك',
    fileName: 'اسم الملف',
    totalAmount: 'الإجمالي',
    employeeCount: 'عدد الموظفين',
    download: 'تحميل',
    close: 'إغلاق',
    loading: 'جاري إنشاء الملف...',
    error: 'فشل إنشاء الملف',
    success: 'تم تحميل الملف بنجاح',
    kwd: 'د.ك',
    employees: 'موظف',
    description: 'هذا الملف يحتوي على تحويلات الرواتب لبنك برقان بالصيغة القياسية.',
  },
};

interface BankFileExportDialogProps {
  open: boolean;
  onClose: () => void;
  payrollId: number;
}

const BankFileExportDialog: React.FC<BankFileExportDialogProps> = ({ open, onClose, payrollId }) => {
  const { language } = useLanguage();
  const t = translations[language];
  const isRTL = language === 'ar';

  const [exportData, setExportData] = useState<BankFileExportData | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      generateFile();
    }
  }, [open, payrollId]);

  const generateFile = async () => {
    setLoading(true);
    try {
      const data = await payrollsService.exportBankFile(payrollId);
      setExportData(data);
    } catch (error) {
      console.error('Failed to generate bank file:', error);
      toast.error(t.error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!exportData) return;

    const blob = new Blob([exportData.content], { type: 'text/plain;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = exportData.fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    toast.success(t.success);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CloudDownload />
          {t.title}
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 4, gap: 2 }}>
            <CircularProgress />
            <Typography>{t.loading}</Typography>
          </Box>
        ) : exportData ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Alert severity="info">{t.description}</Alert>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: '#fff' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography variant="subtitle2">{t.fileName}</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{exportData.fileName}</Typography>
                      </Box>
                      <CloudDownload sx={{ fontSize: 40, opacity: 0.5 }} />
                    </Box>
                  </CardContent>
                </Card>
              </Box>

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ flex: '1 1 250px' }}>
                  <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: '#fff' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box>
                          <Typography variant="subtitle2">{t.totalAmount}</Typography>
                          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                            {exportData.totalAmount.toLocaleString('en-GB', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}
                          </Typography>
                          <Typography variant="caption">{t.kwd}</Typography>
                        </Box>
                        <AttachMoney sx={{ fontSize: 40, opacity: 0.5 }} />
                      </Box>
                    </CardContent>
                  </Card>
                </Box>

                <Box sx={{ flex: '1 1 250px' }}>
                  <Card sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: '#fff' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box>
                          <Typography variant="subtitle2">{t.employeeCount}</Typography>
                          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>{exportData.employeeCount}</Typography>
                          <Typography variant="caption">{t.employees}</Typography>
                        </Box>
                        <People sx={{ fontSize: 40, opacity: 0.5 }} />
                      </Box>
                    </CardContent>
                  </Card>
                </Box>
              </Box>
            </Box>
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t.close}</Button>
        <Button 
          onClick={handleDownload} 
          variant="contained" 
          disabled={!exportData}
          startIcon={<CloudDownload />}
        >
          {t.download}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BankFileExportDialog;